import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Col,
  Row,
  Text,
  ButtonToggle,
  ButtonToggleGroup,
  Search,
} from '@cubeyou-team/hx-react-components';
import { useWindowSize } from '../../utils/hooks';
import {
  StyledTaxonomyExplorer,
  StyledResultContainerWrapper,
  StyledTaxonomyResultsContainer,
  StyledTaxonomyFilters,
  StyledFilters,
} from './TaxonomyExplorer.styles';
import DemographicsContainer from './components/DemographicsContainer';
import ResultsList from './components/ResultsList';
import SavedQueries from './components/SavedQueries';
import { TaxonomySelector } from '../TaxonomySelector';

export const TaxonomyExplorer = ({
  title,
  taxonomySelectorId,
  exploreItems,
  taxonomy,
  shouldCallLoadMore,
  selectedOptionId,
  demographics,
  currentQ,
  onDemographicSelected,
  onItemsSearch,
  onItemDragToggle,
  showModal,
  disableModals,
  onLoadMoreRequest,
  onItemSelected,
  onTaxonomySelected,
  emptyDemographicDataMessage,
  emptyItemResultMessage,
  noConversationsFoundMessage,
  emptyConversationResultsListTitle,
  emptyConversationResultsListSubtitle,
  specifiedComponents,
  onFocus,
  sourceFilters,
  typeFilters,
  onSourceFilterChange,
  onTypeFilterChange,
  initialSource,
  initialType,
  onSavedQuerySelected,
  emptySavedQueriesListTitle,
  emptySavedQueriesListSubtitle,
  className,
  selectedDataSource,
  disabledItemSources,
}) => {
  const size = useWindowSize();
  const [width, setWidth] = useState();
  const [selectedSource, setSelectedSource] = useState();
  const [selectedType, setSelectedType] = useState();
  const isDemographicsSelected = selectedOptionId === 'demographic';
  const isSavedQueriesSelected =
    selectedOptionId === 'saved-queries' ||
    selectedOptionId.includes('folder_');
  const isSearching = currentQ !== '';

  useEffect(() => {
    setSelectedSource(initialSource);
    setSelectedType(initialType);
  }, [initialSource, initialType]);

  const taxonomyFiltersRef = useCallback(
    (node) => {
      if (node !== null) {
        setWidth(node.getBoundingClientRect().width);
      }
    },
    [size]
  );

  const handleOnSearch = useCallback(
    (q) => {
      onItemsSearch(q);
    },
    [onItemsSearch]
  );

  return (
    <StyledTaxonomyExplorer className={className}>
      <Box pB="large" minWidth="100%">
        <Text align="left" variant="dark" weight="bold" size="body">
          {title}
        </Text>
      </Box>
      <StyledTaxonomyFilters ref={taxonomyFiltersRef}>
        <Row>
          <Col span={7}>
            <TaxonomySelector
              id={taxonomySelectorId}
              taxonomy={taxonomy}
              selectedOptionId={selectedOptionId}
              onTaxonomySelected={onTaxonomySelected}
              onFocus={onFocus}
              width={width}
              isQueryBuilder
            />
          </Col>
          <Col span={5}>
            <Search
              delay={500}
              disabled={isDemographicsSelected}
              initialValue={currentQ}
              onSearchChange={handleOnSearch}
              placeholder="Search"
              size="small"
              data-test="item-search"
            />
          </Col>
        </Row>
        {!isDemographicsSelected && (
          <StyledFilters>
            {sourceFilters && (
              <Box pR="large">
                <ButtonToggleGroup
                  selected={selectedSource}
                  onChange={(value) => {
                    onSourceFilterChange(value);
                  }}
                >
                  {sourceFilters.map((source) => (
                    <ButtonToggle
                      key={`source-${source.id}`}
                      value={source.id}
                      disabled={source.disabled}
                    >
                      {source.label}
                    </ButtonToggle>
                  ))}
                </ButtonToggleGroup>
              </Box>
            )}
            {typeFilters && (
              <Box>
                <ButtonToggleGroup
                  selected={selectedType}
                  onChange={(value) => {
                    setSelectedType(value);
                    onTypeFilterChange(value);
                  }}
                >
                  {typeFilters.map((type) => (
                    <ButtonToggle
                      key={`source-${type.id}`}
                      value={type.id}
                      disabled={type.disabled}
                    >
                      {type.label}
                    </ButtonToggle>
                  ))}
                </ButtonToggleGroup>
              </Box>
            )}
          </StyledFilters>
        )}
      </StyledTaxonomyFilters>

      <StyledResultContainerWrapper>
        <StyledTaxonomyResultsContainer data-test="result-container">
          {isDemographicsSelected && (
            <DemographicsContainer
              isSearching={isSearching}
              demographics={demographics}
              onDemographicSelected={onDemographicSelected}
              onItemDragToggle={onItemDragToggle}
              emptyDataMessage={emptyDemographicDataMessage}
              specifiedComponents={specifiedComponents}
            />
          )}

          {!isDemographicsSelected && !isSavedQueriesSelected && (
            <ResultsList
              selectedOptionId={selectedOptionId}
              items={exploreItems.data}
              currentQ={exploreItems.params && exploreItems.params.q}
              onItemDragToggle={onItemDragToggle}
              onItemSelected={onItemSelected}
              showModal={showModal}
              onLoadMoreRequest={onLoadMoreRequest}
              shouldCallLoadMore={shouldCallLoadMore}
              isInitialLoading={exploreItems.isLoading}
              isNextPageLoading={exploreItems.isLoadingMore}
              emptyItemResultMessage={emptyItemResultMessage}
              noConversationsFoundMessage={noConversationsFoundMessage}
              emptyConversationResultsListTitle={
                emptyConversationResultsListTitle
              }
              emptyConversationResultsListSubtitle={
                emptyConversationResultsListSubtitle
              }
              disableModals={disableModals}
              selectedDataSource={selectedDataSource}
              disabledItemSources={disabledItemSources}
            />
          )}
          {isSavedQueriesSelected && (
            <SavedQueries
              items={exploreItems.data}
              onSavedQuerySelected={onSavedQuerySelected}
              onLoadMoreRequest={onLoadMoreRequest}
              shouldCallLoadMore={shouldCallLoadMore}
              isInitialLoading={exploreItems.isLoading}
              isNextPageLoading={exploreItems.isLoadingMore}
              emptyStateTitle={emptySavedQueriesListTitle}
              emptyStateSubtitle={emptySavedQueriesListSubtitle}
            />
          )}
        </StyledTaxonomyResultsContainer>
      </StyledResultContainerWrapper>
    </StyledTaxonomyExplorer>
  );
};

TaxonomyExplorer.propTypes = {
  title: PropTypes.string,
  taxonomySelectorId: PropTypes.string,
  exploreItems: PropTypes.any,
  taxonomy: PropTypes.array,
  shouldCallLoadMore: PropTypes.func,
  selectedOptionId: PropTypes.any,
  demographics: PropTypes.any,
  currentQ: PropTypes.string,
  onDemographicSelected: PropTypes.func,
  onItemsSearch: PropTypes.func,
  onItemDragToggle: PropTypes.func,
  showModal: PropTypes.func,
  disableModals: PropTypes.bool,
  onLoadMoreRequest: PropTypes.func,
  onItemSelected: PropTypes.func,
  onTaxonomySelected: PropTypes.func,
  onFocus: PropTypes.func,
  emptyDemographicDataMessage: PropTypes.string,
  emptyItemResultMessage: PropTypes.string,
  noConversationsFoundMessage: PropTypes.string,
  emptyConversationResultsListTitle: PropTypes.string,
  emptyConversationResultsListSubtitle: PropTypes.string,
  specifiedComponents: PropTypes.any,
  itemTypes: PropTypes.any,
  emptySavedQueriesListTitle: PropTypes.string,
  emptySavedQueriesListSubtitle: PropTypes.string,
  className: PropTypes.string,
  selectedDataSource: PropTypes.string,
  disabledItemSources: PropTypes.arrayOf(PropTypes.string),
};
